import { useTranslation } from 'react-i18next'
import { HugeiconsIcon } from '@hugeicons/react'
import {
    AlertCircleIcon,
    CheckmarkCircle02Icon,
    Delete02Icon,
    Download01Icon,
    File01Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import PropTypes from 'prop-types'

import { DateTime } from '@/common/components'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { ATTACHMENT_SCAN_STATE } from '@/common/utils/Constants/Constants'

import { formatBytes } from './formatBytes'

// Copied from the webapp's QuoteAttachmentRow, minus the staff-only visibility column and the
// uploader name (the web store cannot resolve organisation users).

/** Shared by the row and the panel's column header so the two stay aligned. */
export const ATTACHMENT_ROW_COLUMNS = 'minmax(0, 4fr) minmax(0, 2fr) minmax(130px, 2fr) 96px'

/** Below this width the table scrolls sideways instead of squeezing the name column. */
export const ATTACHMENT_TABLE_MIN_WIDTH = 560

/**
 * AttachmentRow - One attached document as a ledger row: name, uploaded, status, actions.
 * Pending rows offer neither download nor delete; failed rows make remove the action. Delete
 * follows the API's `canDelete`, which folds in ownership and scan state.
 *
 * @param {Object} props
 * @param {Object} props.attachment - DocumentDto row
 * @param {boolean} [props.busy] - Disables controls while a mutation for this row is in flight
 * @param {string} [props.locale]
 * @param {Function} props.onDelete - (attachment) => void
 * @param {Function} props.onDownload - (attachment) => void
 */
const AttachmentRow = ({ attachment, busy = false, locale = 'en-US', onDelete, onDownload }) => {
    const { t } = useTranslation()

    const isPending = attachment.scanState === ATTACHMENT_SCAN_STATE.Pending
    const isFailed = attachment.scanState === ATTACHMENT_SCAN_STATE.Failed
    const isClean = attachment.scanState === ATTACHMENT_SCAN_STATE.Clean
    const canDownload = isClean && Boolean(attachment.downloadUri)

    return (
        <Box
            aria-label={attachment.documentName}
            component="li"
            sx={{
                alignItems: 'center',
                display: 'grid',
                gap: 2,
                gridTemplateColumns: ATTACHMENT_ROW_COLUMNS,
                listStyle: 'none',
                px: 1.5,
                py: 1.25,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: (theme) => (isFailed ? alpha(theme.palette.error.main, 0.06) : 'transparent'),
                borderRadius: isFailed ? 1 : 0,
            }}
        >
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, minWidth: 0 }}>
                <Box
                    component="span"
                    sx={{ color: isFailed ? 'error.main' : 'action.active', display: 'inline-flex' }}
                >
                    <HugeiconsIcon
                        icon={isFailed ? AlertCircleIcon : File01Icon}
                        size={22}
                    />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        fontWeight={500}
                        title={attachment.documentName}
                        variant="body2"
                        noWrap
                    >
                        {attachment.documentName}
                    </Typography>
                    <Typography
                        color={isFailed ? 'error' : 'text.secondary'}
                        variant="caption"
                        noWrap
                    >
                        {isFailed ? t('Could not be accepted. Remove it to free the slot.') : null}
                        {isPending ? t('Still being checked. Refresh the page to see the result.') : null}
                        {isClean ? formatBytes(attachment.sizeBytes) : null}
                    </Typography>
                </Box>
            </Box>

            <Typography
                color="text.secondary"
                variant="body2"
            >
                <DateTime
                    format="DD-MMM-YYYY"
                    locale={locale}
                >
                    {attachment.createDateTime}
                </DateTime>
            </Typography>

            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                {isPending ? (
                    <>
                        <CircularProgress
                            color="secondary"
                            size={18}
                            thickness={5}
                            disableShrink
                        />
                        <Typography
                            color="secondary"
                            variant="body2"
                        >
                            {t('Checking file…')}
                        </Typography>
                    </>
                ) : null}
                {isClean ? (
                    <>
                        <Box
                            component="span"
                            sx={{ color: 'success.main', display: 'inline-flex' }}
                        >
                            <HugeiconsIcon
                                icon={CheckmarkCircle02Icon}
                                size={18}
                            />
                        </Box>
                        <Typography
                            color="success.main"
                            variant="body2"
                        >
                            {t('Ready')}
                        </Typography>
                    </>
                ) : null}
                {isFailed ? (
                    <Typography
                        color="error"
                        fontWeight={500}
                        variant="body2"
                    >
                        {t('Not accepted')}
                    </Typography>
                ) : null}
            </Box>

            <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                {canDownload ? (
                    // The span keeps the tooltip working while the button is disabled.
                    <TbxTooltip title={t('Download')}>
                        <span>
                            <IconButton
                                aria-label={t('Download attachment')}
                                disabled={busy}
                                size="small"
                                onClick={() => onDownload(attachment)}
                            >
                                <HugeiconsIcon
                                    icon={Download01Icon}
                                    size={20}
                                />
                            </IconButton>
                        </span>
                    </TbxTooltip>
                ) : null}
                {attachment.canDelete && isFailed ? (
                    <Button
                        color="error"
                        disabled={busy}
                        size="small"
                        variant="outlined"
                        onClick={() => onDelete(attachment)}
                    >
                        {t('Remove')}
                    </Button>
                ) : null}
                {attachment.canDelete && !isFailed ? (
                    <TbxTooltip title={t('Delete')}>
                        <span>
                            <IconButton
                                aria-label={t('Delete attachment')}
                                disabled={busy}
                                size="small"
                                onClick={() => onDelete(attachment)}
                            >
                                <HugeiconsIcon
                                    icon={Delete02Icon}
                                    size={20}
                                />
                            </IconButton>
                        </span>
                    </TbxTooltip>
                ) : null}
            </Box>
        </Box>
    )
}

AttachmentRow.propTypes = {
    attachment: PropTypes.shape({
        documentId: PropTypes.string.isRequired,
        documentName: PropTypes.string.isRequired,
        canDelete: PropTypes.bool,
        createDateTime: PropTypes.string,
        downloadUri: PropTypes.string,
        scanState: PropTypes.number,
        sizeBytes: PropTypes.number,
    }).isRequired,
    onDelete: PropTypes.func.isRequired,
    onDownload: PropTypes.func.isRequired,
    busy: PropTypes.bool,
    locale: PropTypes.string,
}

export default AttachmentRow
